import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

// ═══════════════════════════════════════════════════════════
// PageLoader — Elegant full-viewport loading indicator
// Always fixed to the viewport centre regardless of DOM position.
// Used for: Suspense lazy-route fallback, feature loading states.
// ═══════════════════════════════════════════════════════════

interface PageLoaderProps {
  /** Optional label shown beneath the shimmer bar */
  label?: string;
  className?: string;
}

export function PageLoader({ label, className }: PageLoaderProps) {
  return (
    <div
      className={cn(
        // Always centred to the viewport — fixed overlay with a very subtle
        // frosted backdrop so it sits cleanly over any content or sidebar.
        "fixed inset-0 z-50 flex flex-col items-center justify-center gap-5",
        "bg-background/60 backdrop-blur-[2px]",
        className,
      )}
    >
      {/* ── App icon with pulse ring ── */}
      <div className="relative">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-500/10 ring-1 ring-inset ring-indigo-500/20">
          <TrendingUp className="h-6 w-6 text-indigo-400" />
        </div>
        <span
          className="absolute inset-0 rounded-2xl ring-2 ring-indigo-400/25 animate-ping"
          style={{ animationDuration: "2s" }}
        />
      </div>

      {/* ── Shimmer bar ── */}
      <div className="relative h-[2px] w-28 overflow-hidden rounded-full bg-border">
        <div className="absolute inset-y-0 w-12 rounded-full bg-gradient-to-r from-transparent via-indigo-400/70 to-transparent animate-[pageloader_1.4s_ease-in-out_infinite]" />
      </div>

      {/* ── Optional label ── */}
      {label && <p className="text-xs text-muted-foreground font-medium tracking-wide">{label}</p>}
    </div>
  );
}
